import type { ApolloClient } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { DatePicker } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import {
  UpdateSourceDatesDocument,
  type UpdateSourceDatesMutation,
} from '~app/graph/generated/graphql'
import i18n from '~app/i18n/i18n'
import { useUpdatingContext } from '../useUpdatingContext'

/*
 * Hooks
 */

export const useSourceDate = (
  sourceId: string
): {
  loading: boolean
  updateSourceDate: (
    newDate: string
  ) => Promise<ApolloClient.MutateResult<UpdateSourceDatesMutation>>
} => {
  const { setUpdating } = useUpdatingContext()
  const [updateSourceDatesMutation, { loading }] = useMutation(
    UpdateSourceDatesDocument
  )
  return {
    loading,
    updateSourceDate: newDate => {
      setUpdating(sourceId, true)
      return updateSourceDatesMutation({
        variables: {
          id: sourceId,
          startAt: dayjs(newDate).startOf('day').toDate(),
        },
      }).finally(() => setUpdating(sourceId, false))
    },
  }
}

/*
 * Component
 */

interface Props {
  sourceId: string
  startDate?: string
}

const StartDate = ({ sourceId: id, startDate }: Props) => {
  const [editing, setEditing] = useState(false)
  const { updateSourceDate, loading } = useSourceDate(id)

  return editing ? (
    <DatePicker
      format={'DD.MM.YYYY'}
      defaultValue={startDate ? dayjs(startDate) : undefined}
      open
      disabled={loading}
      onOpenChange={openState => {
        if (!openState) {
          setEditing(false)
        }
      }}
      onChange={date => {
        updateSourceDate(date.toISOString())
      }}
    />
  ) : (
    <div
      role="button"
      className="editable-cell-value-wrap"
      onClick={() => setEditing(true)}
    >
      {startDate
        ? dayjs(startDate).format('DD.MM.YYYY')
        : i18n.t('misc.noDateSet')}
    </div>
  )
}
export default StartDate
